// Core
import React from 'react';

// Libraries
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';

// Plugins
import { DASHBOARD_NULLISH_TEXT } from '../../../../../../../../plugins/general';
import {
  booleanToQuantityStatus,
  booleanToQuantityText,
  DEVICE_COMMUNICATION_THRESHOLD,
  isEquipmentRunning,
  vsiDriveTypeToText,
} from '../../../../../../../../plugins/crusher';

// Types
import { Theme } from '../../../../../../../../types/theme';

// Components
import { Col } from '../../../../../../../../layout/elements/Col';
import {
  Paragraph,
  ParagraphSize,
  ParagraphWeight,
} from '../../../../../../../../layout/elements/Paragraph';
import { QuantityCard } from '../../../../../../../../layout/elements/QuantityCard';
import { Row } from '../../../../../../../../layout/elements/Row';

// Component
import { InformationProps } from './Information.types';
import {
  ModelBackgroundContainer,
  ModelContainer,
  ModelContentWrapper,
  ModelTitle,
  QuantityCardContainer,
  QuantityContainer,
  QuantityContent,
  QuantityTitle,
} from './Information.style';

function Information({
  className,
  edgeStatus,
  testId,
  unitData,
}: InformationProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();
  const theme: Theme = useTheme();

  // Determine if the equipment is running
  const equipmentRunning: boolean = isEquipmentRunning(
    edgeStatus,
    unitData?.timestamp,
    DEVICE_COMMUNICATION_THRESHOLD,
  );

  return (
    <div className={className} data-testid={testId}>
      <Row>
        <Col cols={6}>
          <ModelContainer>
            <ModelTitle>
              <Paragraph size={ParagraphSize.sm}>
                {t('view.crusher.vsi.maintenance.setup.information.crusherModel')}
              </Paragraph>
            </ModelTitle>
            <ModelBackgroundContainer running={equipmentRunning}>
              <ModelContentWrapper>
                <Paragraph
                  color={
                    equipmentRunning
                      ? theme.color.crusherDashboard.maintenance.setup.information.modelTextColor
                      : theme.color.crusherDashboard.maintenance.setup.information.modelTextColorOff
                  }
                  size={ParagraphSize.sm}
                  weight={ParagraphWeight.bold}
                >
                  {vsiDriveTypeToText(
                    unitData?.crusherModelPLC
                      ? unitData?.crusherModelPLC
                      : DASHBOARD_NULLISH_TEXT,
                  )}
                </Paragraph>
              </ModelContentWrapper>
            </ModelBackgroundContainer>
          </ModelContainer>
        </Col>
        <Col cols={18}>
          <QuantityContainer>
            <QuantityTitle>
              <Paragraph size={ParagraphSize.sm}>
                {t('view.crusher.vsi.maintenance.setup.information.quantity')}
              </Paragraph>
            </QuantityTitle>
            <QuantityContent>
              <Row>
                <Col cols={8}>
                  <QuantityCardContainer>
                    <QuantityCard
                      description={
                        t('view.crusher.vsi.maintenance.setup.information.crusherMotor')
                      }
                      status={booleanToQuantityStatus(
                        unitData?.crusherMotorQty,
                        edgeStatus?.edgeStatus
                          ? edgeStatus?.edgeStatus
                          : null,
                        unitData?.timestamp
                          ? unitData?.timestamp
                          : null,
                      )}
                      quantity={booleanToQuantityText(unitData?.crusherMotorQty)}
                    />
                  </QuantityCardContainer>
                </Col>
              </Row>
            </QuantityContent>
          </QuantityContainer>
        </Col>
      </Row>
    </div>
  );
}

export { Information };
